import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import {
  getPromptId,
  getPromptTitle,
  getSupportedLang,
} from "@/lib/prompt-utils";
import { PromptDTO } from "@shaarp/common";
import { useEffect, useState } from "react";

type DialogSelectTeamPromptProps = {
  title: string;
  /** Ids already present in the target list — hidden so picking one twice is not offered. */
  excludeIds: string[];
  onSelect: (prompt: PromptDTO) => void;
  onClose: () => void;
};

/**
 * A minimal tactic picker restricted to the team's own tactics.
 *
 * Deliberately narrower than `DialogSelectPrompt`: it never lists a personal
 * tactic, because every caller of this dialog hands the selection to someone
 * else — the team's shared default, or another member's pins — and a personal
 * tactic is only its author's to read (criteria #10/#11). `/prompts` still
 * returns the caller's own personal tactics; they are filtered out here
 * rather than requesting a narrower endpoint, since the caller's own personal
 * tactics are the only personal ones `/prompts` can return in the first
 * place — a target member's personal tactics are never sent over the wire.
 * A public tactic is not offered either: the catalogue is retired (#670) and
 * the API refuses it for a default or a member's pin.
 */
export default function DialogSelectTeamPrompt({
  title,
  excludeIds,
  onSelect,
  onClose,
}: DialogSelectTeamPromptProps) {
  const api = useApi();
  const toastError = useToastError();
  const { t, i18n } = useTranslation();
  const lang = getSupportedLang(i18n.language);

  const [prompts, setPrompts] = useState<PromptDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPrompts = async () => {
      setIsLoading(true);
      try {
        const { data } = await api.get<PromptDTO[]>("/prompts");
        setPrompts(data);
      } catch {
        toastError();
        setPrompts([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPrompts();
  }, [api, toastError]);

  const excludeSet = new Set(excludeIds);
  const selectablePrompts = prompts.filter(
    (p) => p.ownerType === "team" && !excludeSet.has(getPromptId(p)),
  );

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[600px] w-full">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <Separator className="my-4" />
        <div className="overflow-auto max-h-[60vh]">
          <Command>
            <CommandInput placeholder={t("common.search")} />
            <CommandList>
              {!isLoading && (
                <CommandEmpty>{t("common.noResults")}</CommandEmpty>
              )}
              <CommandGroup>
                {selectablePrompts.map((prompt) => (
                  <CommandItem
                    key={getPromptId(prompt)}
                    value={getPromptTitle(prompt, lang)}
                    onSelect={() => {
                      onSelect(prompt);
                      onClose();
                    }}
                    className="flex items-center justify-between gap-2 cursor-pointer"
                  >
                    <span className="truncate">
                      {getPromptTitle(prompt, lang)}
                    </span>
                    <Badge
                      variant={prompt.isAutoEnabled ? "outline" : "secondary"}
                      className="shrink-0 font-normal"
                      title={
                        prompt.isAutoEnabled
                          ? t("prompt.card.autoUseTooltip")
                          : undefined
                      }
                    >
                      {prompt.isAutoEnabled
                        ? t("prompt.card.autoUse")
                        : t("prompt.card.manualUse")}
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
        <Separator className="my-4" />
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="ghost">
              {t("prompt.library.buttons.close")}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
